import { useCallback, useState } from 'react';

import { ApiFehler, KassenApi, NULL_BETRAEGE } from '../api';
import {
  eingabeAusKonfig,
  konfigAusStamm,
  speichereStammCache,
  vollabgleichSperre,
  type SchnellwahlKonfig,
} from '../schnellwahl';
import { stammAblage } from '../stamm-speicher';
import { verkaufsKonfig } from '../verkaufsstamm';
import {
  entferneBediener,
  fuegeBedienerHinzu,
  ladeBediener,
  speichereBediener,
  type BedienerStand,
} from '../bediener';
import {
  oeffneKundenanzeige,
  type FensterUmgebung,
  type OeffnenErgebnis,
} from '../kundenanzeige-fenster';
import { GeraeteEinstellungen } from './GeraeteEinstellungen';
import { SchnellwahlEditor } from './Schnellwahl';

const ANZEIGE_HINWEIS: Record<OeffnenErgebnis, string | null> = {
  'zweiter-bildschirm':
    'Kundenanzeige auf den zweiten Bildschirm geschickt – bitte prüfen, ob sie dort steht; Doppelklick schaltet Vollbild.',
  'ohne-bildschirmwahl':
    'Kein zweiter Bildschirm erkannt, Fensterverwaltung nicht erlaubt oder Verschieben abgelehnt: Fenster von Hand auf den Kundenbildschirm ziehen. Windows-Anzeige auf „Erweitern“ stellen.',
  blockiert: 'Fenster wurde blockiert – bitte noch einmal „Kundenanzeige öffnen“ tippen.',
};

/**
 * Kasse auswählen oder neu anlegen (Stammdaten, Zahlungsweg), Kassenschlüssel
 * hinterlegen und den Startbeleg erstellen. Die Kassen-ID und der
 * Kassenschlüssel bleiben auf dem Gerät; das Betreiber-Token wird nur für das
 * Anlegen verwendet und nie gespeichert.
 */
export function Einrichtung(props: {
  api: KassenApi;
  basisUrl: string;
  kassenId: string;
  kassenschluessel: string;
  onKasse: (kassenId: string) => void;
  onSchluessel: (schluessel: string) => void;
  /** Modul Kundenanzeige gebucht (Standard: ja). */
  kundenanzeige?: boolean;
  /** Modul Küchenbons gebucht. */
  kuechenbons?: boolean;
  /** Kasse hat eine Hardware-Bridge (Bondrucker, Lade, ZVT). */
  bridgeZugelassen?: boolean;
}) {
  const { api } = props;
  const [anzeigeHinweis, setAnzeigeHinweis] = useState<string | null>(null);
  const [kassenId, setKassenId] = useState(props.kassenId);
  const [schluessel, setSchluessel] = useState(props.kassenschluessel);
  const [betreiberToken, setBetreiberToken] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [bediener, setBediener] = useState<BedienerStand>(() =>
    props.kassenId === '' ? { liste: [], aktiv: null } : ladeBediener(localStorage, props.kassenId),
  );
  const [neuerBediener, setNeuerBediener] = useState('');
  function bedienerSpeichern(stand: BedienerStand): void {
    setBediener(stand);
    if (props.kassenId !== '') {
      speichereBediener(localStorage, props.kassenId, stand);
    }
  }
  const [form, setForm] = useState({
    unternehmerId: '',
    name: '',
    anschrift: '',
    uid: '',
    zahlungsanbieter: '' as '' | 'sumup' | 'zvt' | 'dev',
    zahlungsterminal: '',
  });

  const betreiberApi = (): KassenApi =>
    new KassenApi({ basisUrl: props.basisUrl, token: () => betreiberToken.trim() || null });

  function schluesselUebernehmen(): void {
    props.onSchluessel(schluessel.trim());
    setMeldung(
      schluessel.trim() === '' ? 'Kassenschlüssel entfernt.' : 'Kassenschlüssel gespeichert.',
    );
  }

  async function pruefen(): Promise<void> {
    setFehler(null);
    setMeldung(null);
    props.onSchluessel(schluessel.trim());
    try {
      const s = await api.status(kassenId.trim());
      props.onKasse(kassenId.trim());
      setMeldung(
        s.letzterBeleg === null
          ? 'Kasse vorhanden, aber ohne Startbeleg – bitte Startbeleg erstellen.'
          : `Kasse verbunden (letzter Beleg ${s.letzterBeleg.belegnummer}).`,
      );
      setNeu(false);
    } catch (e: unknown) {
      if (e instanceof ApiFehler && e.status === 404) {
        setNeu(true);
        setMeldung('Kasse unbekannt – mit Betreiber-Token anlegen.');
      } else if (e instanceof ApiFehler && (e.status === 401 || e.status === 403)) {
        setFehler(
          'Kein gültiger Kassenschlüssel für diese Kasse. Schlüssel eintragen oder die Kasse mit Betreiber-Token anlegen.',
        );
        setNeu(true);
      } else {
        setFehler(e instanceof Error ? e.message : String(e));
      }
    }
  }

  async function anlegen(): Promise<void> {
    setFehler(null);
    try {
      const admin = betreiberApi();
      const id = kassenId.trim();
      await admin.kasseAnlegen({
        kassenId: id,
        unternehmerId: form.unternehmerId.trim(),
        unternehmer: {
          name: form.name.trim(),
          anschrift: form.anschrift.trim(),
          ...(form.uid.trim() === '' ? {} : { uid: form.uid.trim() }),
        },
        ...(form.zahlungsterminal.trim() === ''
          ? {}
          : { zahlungsterminal: form.zahlungsterminal.trim() }),
        ...(form.zahlungsanbieter === '' ? {} : { zahlungsanbieter: form.zahlungsanbieter }),
      });
      const neuerSchluessel = await admin.kassenschluesselAusstellen(id);
      setSchluessel(neuerSchluessel);
      props.onSchluessel(neuerSchluessel);
      props.onKasse(id);
      setNeu(false);
      // Token bleibt eingetragen, solange die Seite offen ist (z. B. für den Artikelstamm); gespeichert wird es nie.
      setMeldung(
        'Kasse angelegt, Kassenschlüssel auf diesem Gerät gespeichert – jetzt den Startbeleg erstellen.',
      );
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function schluesselNeu(): Promise<void> {
    setFehler(null);
    try {
      const neuerSchluessel = await betreiberApi().kassenschluesselAusstellen(kassenId.trim());
      setSchluessel(neuerSchluessel);
      props.onSchluessel(neuerSchluessel);
      props.onKasse(kassenId.trim());
      // Token bleibt eingetragen, solange die Seite offen ist (z. B. für den Artikelstamm); gespeichert wird es nie.
      setMeldung(
        'Neuer Kassenschlüssel ausgestellt und gespeichert (alte Schlüssel sind ungültig).',
      );
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function startbeleg(): Promise<void> {
    setFehler(null);
    try {
      const b = await api.erstelleBeleg(kassenId.trim(), {
        belegtyp: 'START',
        belegnummer: 'S-1',
        betraege: NULL_BETRAEGE,
      });
      setMeldung(
        `Startbeleg ${b.belegnummer} erstellt. Nächste Schritte: Kasse in FinanzOnline registrieren und den Startbeleg binnen einer Woche prüfen (Belegcheck-App).`,
      );
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  const ungueltigeId = kassenId.includes('_');

  async function stammUebertragen(konfig: SchnellwahlKonfig): Promise<string> {
    if (betreiberToken.trim() === '') {
      throw new Error(
        'Betreiber-Token eintragen (Abschnitt „Betreiber“), um den Artikelstamm zu übertragen.',
      );
    }
    const s = await api.status(props.kassenId);
    if (s.unternehmerId === null) {
      throw new Error('Kasse hat keinen Unternehmer – zuerst Kasse anlegen.');
    }
    // P2: Nur mit dem Datenstand übertragen, auf dem genau diese Schnellwahl beruht – ein
    // veralteter Gerätestand darf nie neuere Daten aus Backoffice, Import oder einer anderen
    // Kasse überschreiben (Server: 409).
    let zuSenden = konfig;
    if (konfig.basisVersion === undefined) {
      try {
        await api.artikelstamm(props.kassenId);
        throw new Error(
          'Datenstand dieser Schnellwahl unbekannt – zuerst „Vom Server laden“, dann anpassen und übertragen.',
        );
      } catch (e: unknown) {
        if (!(e instanceof ApiFehler && e.status === 404)) {
          throw e;
        }
        zuSenden = { ...konfig, basisVersion: 0 }; // noch kein Stamm am Server
      }
    }
    const eingabe = eingabeAusKonfig(zuSenden);
    // PB-6: Vollabgleich bei großem Stamm nie senden (auch wenn die Taste umgangen wird).
    const gesperrt = vollabgleichSperre(eingabe);
    if (gesperrt !== null) {
      throw new Error(gesperrt);
    }
    let stamm: Awaited<ReturnType<KassenApi['artikelstammErsetzen']>>;
    try {
      stamm = await betreiberApi().artikelstammErsetzen(s.unternehmerId, eingabe);
    } catch (e: unknown) {
      if (e instanceof ApiFehler && e.status === 409) {
        throw new Error(
          `${e.message}. Die Schnellwahl dieser Kasse ist veraltet: „Vom Server laden“ übernimmt den aktuellen Stand (lokale Änderungen gehen dabei verloren).`,
          { cause: e },
        );
      }
      throw e;
    }
    // Zwischenspeicher des Verkaufs sofort nachziehen, damit die neuen Kacheln ohne Neuladen
    // erscheinen – mit dem Profil der Kasse (P10), sonst übernimmt der Sync-Dienst.
    const verkauf = await verkaufsKonfig(api, props.kassenId, stamm);
    if (verkauf !== null) {
      speichereStammCache(
        localStorage,
        props.kassenId,
        { version: stamm.version, konfig: verkauf },
        stammAblage(),
      );
    }
    return `Artikelstamm Version ${String(stamm.version)} für Unternehmer ${s.unternehmerId} gespeichert – alle Kassen laden ihn beim nächsten Start.`;
  }

  // Stabil über Renderdurchläufe: der Editor lädt damit beim Öffnen den Serverstand.
  const aktuelleKasse = props.kassenId;
  const stammVomServer = useCallback(async (): Promise<SchnellwahlKonfig | null> => {
    try {
      const stamm = await api.artikelstamm(aktuelleKasse);
      // Der Editor bearbeitet den Stamm selbst (ohne Profil) …
      const konfig = konfigAusStamm(stamm);
      // … der Verkauf zeigt ihn mit dem Profil der Kasse (P10); ohne ermittelbares Profil
      // leitet der Sync-Dienst die Ansicht ab (Agent-3-/Codex-Befund).
      const verkauf = await verkaufsKonfig(api, aktuelleKasse, stamm);
      if (verkauf !== null) {
        speichereStammCache(
          localStorage,
          aktuelleKasse,
          { version: stamm.version, konfig: verkauf },
          stammAblage(),
        );
      }
      return konfig;
    } catch (e: unknown) {
      if (e instanceof ApiFehler && e.status === 404) {
        return null;
      }
      throw e;
    }
  }, [api, aktuelleKasse]);

  return (
    <>
      {props.kassenId !== '' && (
        <SchnellwahlEditor
          key={props.kassenId}
          kassenId={props.kassenId}
          onUebertragen={stammUebertragen}
          onVomServer={stammVomServer}
        />
      )}
      <div className="karte">
        <div className="zeile">
          <label>
            Kassen-ID
            <input
              value={kassenId}
              onChange={(e) => {
                setKassenId(e.target.value);
              }}
              placeholder="z. B. FILIALE1-KASSA1"
              style={{ minWidth: '16rem' }}
            />
          </label>
          <label>
            Kassenschlüssel (dieses Gerät)
            <input
              type="password"
              value={schluessel}
              onChange={(e) => {
                setSchluessel(e.target.value);
              }}
              placeholder="KASSEN-ID.…"
              style={{ minWidth: '18rem' }}
            />
          </label>
          <button className="taste zweit" onClick={schluesselUebernehmen}>
            Schlüssel speichern
          </button>
          <button
            className="taste"
            disabled={kassenId.trim() === '' || ungueltigeId}
            onClick={() => {
              void pruefen();
            }}
          >
            Verbinden
          </button>
          <button
            className="taste zweit"
            disabled={kassenId.trim() === ''}
            onClick={() => {
              void startbeleg();
            }}
          >
            Startbeleg erstellen
          </button>
        </div>
        {ungueltigeId && <p className="fehler">Die Kassen-ID darf keinen Unterstrich enthalten.</p>}
        {meldung !== null && <p className="ok">{meldung}</p>}
        {fehler !== null && <p className="fehler">{fehler}</p>}
      </div>

      {props.kassenId !== '' && (
        <GeraeteEinstellungen
          key={`geraete-${props.kassenId}`}
          api={api}
          kassenId={props.kassenId}
          bridgeZugelassen={props.bridgeZugelassen === true}
        />
      )}

      {props.kassenId !== '' && props.kuechenbons === true && (
        <div className="karte">
          <h3>Küchenmonitor</h3>
          <p className="hinweis">
            Für ein Tablet oder einen Bildschirm in Küche oder Schank: zeigt die bonierten
            Bestellungen, älteste zuerst, mit „Fertig“ zum Abhaken. Das Gerät wie eine Kasse
            verbinden (Kassen-ID und Kassenschlüssel), dann diese Ansicht öffnen und die
            Produktionsstelle wählen. Drucker für Küchenbons stellt der Betreiber im Backoffice ein.
          </p>
          <a className="taste zweit" href="?kueche=1" target="_blank" rel="noopener">
            Küchenmonitor öffnen
          </a>
        </div>
      )}

      {props.kassenId !== '' && props.kundenanzeige !== false && (
        <div className="karte">
          <h3>Kundenanzeige</h3>
          <p className="hinweis">
            Zweites Fenster für den kundenseitigen Bildschirm (z. B. Posiflex 1024 × 768 als
            erweiterter Windows-Desktop): laufender Bon, Zahlungsstand und Gezahlt/Rückgeld. Die
            Anzeige gehört fest zu Kasse {props.kassenId} und läuft im selben Browser wie die Kasse.
            Beim ersten Öffnen „Fensterverwaltung“ erlauben, dann liegt sie direkt auf dem zweiten
            Bildschirm; Doppelklick darauf schaltet Vollbild.
          </p>
          <button
            type="button"
            className="taste zweit"
            onClick={() => {
              void oeffneKundenanzeige(props.kassenId, window as unknown as FensterUmgebung).then(
                (ergebnis) => {
                  setAnzeigeHinweis(ANZEIGE_HINWEIS[ergebnis]);
                },
              );
            }}
          >
            Kundenanzeige öffnen
          </button>
          {anzeigeHinweis !== null && <p className="hinweis">{anzeigeHinweis}</p>}
        </div>
      )}

      {props.kassenId !== '' && (
        <div className="karte">
          <h3>Bediener (Kassierkräfte)</h3>
          <p className="hinweis">
            Namen dieses Geräts; die aktive Kassierkraft wird im Verkauf gewählt und steht am Beleg,
            im Journal und im Tagesbericht.
          </p>
          <div className="chips">
            {bediener.liste.map((n) => (
              <button
                key={n}
                type="button"
                className={bediener.aktiv === n ? 'aktiv' : ''}
                title="Entfernen"
                onClick={() => {
                  if (window.confirm(`Bediener „${n}“ entfernen?`)) {
                    bedienerSpeichern(entferneBediener(bediener, n));
                  }
                }}
              >
                {n} ×
              </button>
            ))}
            {bediener.liste.length === 0 && <span className="hinweis">Noch keine Bediener.</span>}
          </div>
          <div className="zeile">
            <label>
              Name
              <input
                value={neuerBediener}
                maxLength={60}
                onChange={(e) => {
                  setNeuerBediener(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    bedienerSpeichern(fuegeBedienerHinzu(bediener, neuerBediener));
                    setNeuerBediener('');
                  }
                }}
              />
            </label>
            <button
              className="taste zweit"
              disabled={neuerBediener.trim() === ''}
              onClick={() => {
                bedienerSpeichern(fuegeBedienerHinzu(bediener, neuerBediener));
                setNeuerBediener('');
              }}
            >
              Hinzufügen
            </button>
          </div>
        </div>
      )}

      <div className="karte">
        <h3>Betreiber (nur zum Einrichten)</h3>
        <div className="zeile">
          <label>
            Betreiber-Token
            <input
              type="password"
              value={betreiberToken}
              onChange={(e) => {
                setBetreiberToken(e.target.value);
              }}
              style={{ minWidth: '18rem' }}
              autoComplete="off"
            />
          </label>
          <button
            className="taste zweit"
            disabled={betreiberToken.trim() === '' || kassenId.trim() === ''}
            onClick={() => {
              void schluesselNeu();
            }}
          >
            Kassenschlüssel neu ausstellen
          </button>
        </div>
        <p className="hinweis">
          Das Betreiber-Token wird nicht gespeichert. Es wird für das Anlegen einer Kasse und das
          Ausstellen des Kassenschlüssels gebraucht; der Kassenbetrieb selbst läuft mit dem
          Kassenschlüssel.
        </p>
        {neu && (
          <>
            <h3>Kasse anlegen</h3>
            <div className="zeile">
              <label>
                Unternehmer-ID
                <input
                  value={form.unternehmerId}
                  onChange={(e) => {
                    setForm({ ...form, unternehmerId: e.target.value });
                  }}
                />
              </label>
              <label>
                Firma / Name
                <input
                  value={form.name}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                  }}
                  style={{ minWidth: '16rem' }}
                />
              </label>
              <label>
                Anschrift
                <input
                  value={form.anschrift}
                  onChange={(e) => {
                    setForm({ ...form, anschrift: e.target.value });
                  }}
                  style={{ minWidth: '18rem' }}
                />
              </label>
              <label>
                UID (für Rechnungen über 400 €)
                <input
                  value={form.uid}
                  onChange={(e) => {
                    setForm({ ...form, uid: e.target.value });
                  }}
                />
              </label>
            </div>
            <div className="zeile" style={{ marginTop: '0.6rem' }}>
              <label>
                Zahlungsweg
                <select
                  value={form.zahlungsanbieter}
                  onChange={(e) => {
                    setForm({
                      ...form,
                      zahlungsanbieter: e.target.value as typeof form.zahlungsanbieter,
                    });
                  }}
                >
                  <option value="">Standard der Installation</option>
                  <option value="sumup">SumUp (Cloud)</option>
                  <option value="zvt">ZVT-Terminal über Bridge (hobex, PAYONE)</option>
                  <option value="dev">Entwicklung (Fake)</option>
                </select>
              </label>
              <label>
                Terminal (SumUp Reader-ID / Terminalname)
                <input
                  value={form.zahlungsterminal}
                  onChange={(e) => {
                    setForm({ ...form, zahlungsterminal: e.target.value });
                  }}
                  style={{ minWidth: '16rem' }}
                />
              </label>
              <button
                className="taste"
                disabled={
                  betreiberToken.trim() === '' ||
                  form.unternehmerId.trim() === '' ||
                  form.name.trim() === '' ||
                  form.anschrift.trim() === ''
                }
                onClick={() => {
                  void anlegen();
                }}
              >
                Anlegen
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
